import {useCallback, useEffect, useMemo, useState} from 'react';
import {Box} from '@mui/material';
import {CommonTable} from '@/wp/basic/common';
import {SnackbarField} from '@/components/common/SnackbarField';
import {FsSelectionDrawer, createShowSnackbar, calculateAmount} from '@/adjustment/adjust/support';
import {AdjustmentDetailsDrawer} from '@/adjustment/adjust/dialogs';
import {mergeDataForAdjust} from '@/adjustment/adjust/dialogs/utils/adjustment-helpers';
import {determineButtonMode, getAdjustmentTotal} from '@/adjustment/adjust/utils/adjustmentLockUtils';
import {useAdjustDraftStore, useFsAdjustStore} from '@/store/audit';
import {useHandleFsSelection} from '@/adjustment/hooks/useHandleFsSelection';
import {useAdjustmentDetailsDrawer} from '@/adjustment/hooks/useAdjustmentDetailsDrawer';
import {AdjustmentMode} from '@/services/auditService/adjustmentModes';
import type {FsCategoryLine} from '@/store/audit/fsCategoryStore';
import type {AdjustmentDetail, CommonRow} from '@/services/auditService/interface/wp-row';
import {resolveLoanFieldAdjItemName} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/loanFieldAdjUtils';

export type LoanFieldAdjustTarget = {
    fromCode: string;
    itemName: string;
    fieldLabel?: string;
    perClient: number;
    existingAdjustments?: AdjustmentDetail[] | null;
    statement?: 'BS' | 'PL';
};

/** Parent DialogActions: show Adjustment when user adj ≠ 0; hide Save */
export type LoanFieldAdjustActionState = {
    shouldShowAdjustmentButton: boolean;
    isProcessing: boolean;
    submitAdjustment: () => void;
};

export type LoanFieldAdjustPanelProps = {
    /** When null, panel is hidden */
    target: LoanFieldAdjustTarget | null;
    onClose: () => void;
    companyId?: number;
    annualId?: number;
    currency?: string;
    /** Close parent loan dialog after WP→WP starts */
    onParentClose?: () => void;
    /** Notify parent to swap Save ↔ Adjustment in DialogActions */
    onActionStateChange?: (state: LoanFieldAdjustActionState | null) => void;
    /**
     * Build save-shaped WP details for WP→WP (same as BankLoan/VehicleLoan save).
     * Panel adds `adjustment` (user field adj) under bankLoans / vehicleLoans details.
     */
    buildLoanSaveDetails?: () => Record<string, any>;
};

/**
 * Inline loan field adjustment panel (above HKFRS SME-FRS in BankLoan / VehicleLoan).
 * No title chrome; Per client header fixed as Client; Final trailing cancel icon closes panel.
 * Adjustment button lives on parent DialogActions when user adj ≠ 0.
 */
export default function LoanFieldAdjustPanel({
    target,
    onClose,
    companyId,
    annualId,
    currency: currencyProp,
    onParentClose,
    onActionStateChange,
    buildLoanSaveDetails,
}: LoanFieldAdjustPanelProps) {
    const getLineByCode = useFsAdjustStore((s) => s.getLineByCode);
    const adjustDraftStore = useAdjustDraftStore();
    const derivedAnnualId = Number(annualId) || 0;
    const currency = currencyProp || 'HKD';
    const adjDrawer = useAdjustmentDetailsDrawer();

    const open = !!target;
    const fromCode = target?.fromCode || '';
    const itemName = target?.itemName || '';
    const fieldLabel = target?.fieldLabel;
    const perClient = target?.perClient ?? 0;
    const existingAdjustments = target?.existingAdjustments;
    const statementProp = target?.statement;

    const line = useMemo((): FsCategoryLine => {
        if (!fromCode) {
            return {code: '', name: '', scheduleName: '', entry: 'Dr'} as unknown as FsCategoryLine;
        }
        const fsLine = getLineByCode(fromCode);
        if (!fsLine) {
            return {
                code: fromCode,
                name: fieldLabel || itemName,
                scheduleName: fieldLabel || itemName,
                entry: 'Dr',
            } as unknown as FsCategoryLine;
        }
        return {
            ...fsLine,
            code: fsLine.code || fromCode,
            name: fsLine.name || fieldLabel || itemName,
        } as unknown as FsCategoryLine;
    }, [fromCode, itemName, fieldLabel, getLineByCode]);

    const statement = useMemo((): 'BS' | 'PL' => {
        if (statementProp === 'BS' || statementProp === 'PL') return statementProp;
        return String(fromCode).toUpperCase().startsWith('PL') ? 'PL' : 'BS';
    }, [fromCode, statementProp]);

    const [rows, setRows] = useState<CommonRow[]>([]);
    const [pendingAdjustments, setPendingAdjustments] = useState<Record<number, number>>({});
    const [fsDrawerOpen, setFsDrawerOpen] = useState(false);
    const [toPaperSelected, setToPaperSelected] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'warning' | 'error' | 'success'>('warning');

    const showSnackbar = useMemo(
        () => createShowSnackbar(setSnackbarMessage, setSnackbarSeverity, setOpenSnackbar),
        [],
    );

    const normalizedExisting = useMemo(() => {
        if (!Array.isArray(existingAdjustments) || existingAdjustments.length === 0) return null;
        return existingAdjustments.map((a) => ({
            adjustHeaderId: String(a.adjustHeaderId ?? ""),
            refNo: a.refNo || '',
            adjustment: Number(a.adjustment) || 0,
            adjustmentMode: a.adjustmentMode,
        }));
    }, [existingAdjustments]);

    // Item = mapped FS node label (Bank loan, secured / Obligation under finance lease / Loan interest expenses)
    const displayItemName = resolveLoanFieldAdjItemName(fromCode, itemName);

    useEffect(() => {
        if (!open || !target) return;
        const row: CommonRow = {
            id: 1,
            item: displayItemName,
            perClient: Number(perClient) || 0,
            lastYearFinal: 0,
        };
        if (normalizedExisting) {
            row.adjustments = normalizedExisting;
        } else {
            row.adjustment = 0;
        }
        setRows([row]);
        setPendingAdjustments({});
        setFsDrawerOpen(false);
        setToPaperSelected(false);
        setIsProcessing(false);
    }, [open, target, displayItemName, perClient, normalizedExisting]);

    const amountDifference = useMemo(() => {
        return calculateAmount(rows) + Object.values(pendingAdjustments).reduce((sum, val) => sum + val, 0);
    }, [rows, pendingAdjustments]);

    const buttonMode = useMemo(
        () => determineButtonMode(rows, pendingAdjustments),
        [rows, pendingAdjustments],
    );
    const shouldShowAdjustmentButton = buttonMode === 'adjustment';

    const mergeDataForAdjustMode = useCallback((): any => {
        // User field adjustment only (excludes locked historical adjustments[])
        const fieldAdjustment =
            calculateAmount(rows)
            + Object.values(pendingAdjustments).reduce((sum, val) => sum + val, 0);

        // Save-shaped details + top-level adjustment (backend maps by line.code → curr/nonCurr/interest)
        const saveDetails = typeof buildLoanSaveDetails === 'function' ? (buildLoanSaveDetails() || {}) : {};
        const detailsData: Record<string, any> = {
            ...saveDetails,
            adjustment: fieldAdjustment,
        };

        const wp2wpResult = mergeDataForAdjust({
            categoryLine: line,
            payloadLine: line,
            showSnackbar,
            statement,
            companyId: Number(companyId) || 0,
            annualId: derivedAnnualId,
            currency,
            entry: line?.entry,
            totalsFinal: rows.reduce((sum, r) => {
                return sum + (Number(r.perClient) || 0) + getAdjustmentTotal(r);
            }, 0) + Object.values(pendingAdjustments).reduce((sum, val) => sum + val, 0),
            amountDifference: fieldAdjustment,
            detailsData,
        });

        if (!wp2wpResult) return null;
        if (wp2wpResult.line) {
            wp2wpResult.line.code = fromCode;
            (wp2wpResult.line as any).scheduleCode = fromCode;
            // Keep absolute amount on line (backend also reads details.adjustment for attrs)
            wp2wpResult.line.adjustmentAmount = Math.abs(fieldAdjustment);
            wp2wpResult.line.adjustment = fieldAdjustment;
            // Ensure details keep save shape + adjustment after merge cleanup
            const detail0 = Array.isArray(wp2wpResult.line.details) ? wp2wpResult.line.details[0] : null;
            if (detail0 && typeof detail0 === 'object') {
                wp2wpResult.line.details = [{
                    ...detail0,
                    ...saveDetails,
                    adjustment: fieldAdjustment,
                }];
            }
        }
        return wp2wpResult.line;
    }, [
        rows, pendingAdjustments, line, showSnackbar, statement,
        companyId, derivedAnnualId, currency, fromCode, buildLoanSaveDetails,
    ]);

    const handleAdjustmentClick = useCallback(() => {
        if (isProcessing) return;
        setIsProcessing(true);
        try {
            const result = mergeDataForAdjustMode();
            if (!result) {
                showSnackbar('Failed to prepare adjustment data', 'error');
                setIsProcessing(false);
                return;
            }
            adjustDraftStore.setFromPaperFromLine(result, Number(companyId) || 0, derivedAnnualId);
            setFsDrawerOpen(true);
        } catch (error) {
            showSnackbar(`Error: ${error}`, 'error');
        } finally {
            setIsProcessing(false);
        }
    }, [
        isProcessing, mergeDataForAdjustMode, adjustDraftStore,
        companyId, derivedAnnualId, showSnackbar,
    ]);

    const handleFsSelection = useHandleFsSelection({
        selectedAdjustmentMode: AdjustmentMode.ADJUST_WP_TO_WP,
        mergeData: mergeDataForAdjustMode,
        showSnackbar,
        companyId: Number(companyId) || 0,
        annualId: derivedAnnualId,
        setFsDrawerOpen,
        setToPaperSelected,
        onClose,
    });

    useEffect(() => {
        if (!fsDrawerOpen && toPaperSelected) {
            const adjustment = adjustDraftStore.getAdjustment();
            if (adjustment.fromPaper && adjustment.toPaper) {
                adjustDraftStore.setAdjustTabsDialogOpen(true);
                setToPaperSelected(false);
                onClose();
                onParentClose?.();
            }
        }
    }, [fsDrawerOpen, toPaperSelected, onClose, onParentClose, adjustDraftStore]);

    // Sync Save ↔ Adjustment with parent DialogActions
    useEffect(() => {
        if (!open) {
            onActionStateChange?.(null);
            return;
        }
        onActionStateChange?.({
            shouldShowAdjustmentButton,
            isProcessing,
            submitAdjustment: handleAdjustmentClick,
        });
    }, [open, shouldShowAdjustmentButton, isProcessing, handleAdjustmentClick, onActionStateChange]);

    useEffect(() => {
        return () => {
            onActionStateChange?.(null);
        };
    }, [onActionStateChange]);

    if (!open || !target) return null;

    return (
        <>
            <Box
                sx={{
                    mt: 2,
                    mb: 1,
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                }}
            >
                <Box sx={{width: '100%'}}>
                    <CommonTable
                        rows={rows}
                        onRowsChange={setRows}
                        yoa=""
                        lastYoa=""
                        currency={currency}
                        code={fromCode}
                        allowAddRow={false}
                        hideLastYearColumn
                        hideTotalFooter
                        isItemEditable={() => false}
                        isPerClientEditable={() => false}
                        isRowDeletable={() => false}
                        onNewAdjustmentPending={setPendingAdjustments}
                        onAdjustmentDrawerOpen={adjDrawer.handleOpen}
                        perClientColumnTitle="Client"
                        onFinalCancel={onClose}
                    />
                </Box>
            </Box>
            <SnackbarField
                open={openSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
                autoHideDuration={4000}
                onClose={() => setOpenSnackbar(false)}
            />
            <FsSelectionDrawer
                open={fsDrawerOpen}
                onClose={() => setFsDrawerOpen(false)}
                onConfirm={handleFsSelection}
                payload={{
                    companyId: Number(companyId),
                    annualId: derivedAnnualId,
                    title: 'Select Target Schedule for Adjustment',
                }}
                adjustmentMode={AdjustmentMode.ADJUST_WP_TO_WP}
            />
            <AdjustmentDetailsDrawer
                open={adjDrawer.open}
                onClose={adjDrawer.handleClose}
                itemName={adjDrawer.itemName}
                currency={currency}
                headerData={adjDrawer.headerData}
                headerDataLoading={adjDrawer.loading}
            />
        </>
    );
}
